import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldAlert, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user } = useAuth();
    const [enabled, setEnabled] = useState(false);
    const [setup, setSetup] = useState(null); // {secret, otpauth_uri, qr_code_data_url}
    const [token, setToken] = useState("");
    const [disableToken, setDisableToken] = useState("");

    const loadStatus = async () => {
        const { data } = await api.get("/2fa/status");
        setEnabled(data.enabled);
    };
    useEffect(() => { loadStatus(); }, []);

    const startSetup = async () => {
        try { const { data } = await api.post("/2fa/setup"); setSetup(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const verify = async () => {
        try {
            await api.post("/2fa/verify", { token });
            toast.success("2FA enabled!");
            setSetup(null); setToken(""); loadStatus();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const disable = async () => {
        try {
            await api.post("/2fa/disable", { token: disableToken });
            toast.success("2FA disabled"); setDisableToken(""); loadStatus();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Profile & Security" subtitle="Account settings and 2FA">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-4">Account</div>
                    <div className="space-y-3">
                        <div><Label className="text-xs uppercase tracking-wider text-slate-500">Full Name</Label>
                            <div className="text-sm font-medium mt-1">{user?.name}</div></div>
                        <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                            <div className="text-sm font-mono mt-1">{user?.email}</div></div>
                        <div><Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
                            <div className="mt-1"><span className="status-badge bg-slate-100 text-slate-700 border-slate-200">{user?.role?.replace(/_/g, " ")}</span></div></div>
                    </div>
                </Card>

                <Card className="p-6 border border-slate-200 rounded-md shadow-none">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Two-Factor Authentication</div>
                            <div className="text-sm font-medium mt-1 flex items-center gap-2">
                                {enabled ? <><ShieldCheck className="w-4 h-4 text-emerald-600" /> Enabled</> : <><ShieldAlert className="w-4 h-4 text-slate-400" /> Not enabled</>}
                            </div>
                        </div>
                    </div>

                    {!enabled && !setup && (
                        <div>
                            <p className="text-sm text-slate-600 mb-3">Add an extra layer of security using an authenticator app (Google Authenticator, Authy, 1Password).</p>
                            <Button onClick={startSetup} className="bg-[#0A1C3E] rounded-sm" data-testid="enable-2fa">
                                Enable 2FA
                            </Button>
                        </div>
                    )}

                    {setup && (
                        <div className="space-y-3">
                            <p className="text-sm text-slate-600">Scan the QR with your authenticator app:</p>
                            <div className="flex justify-center bg-slate-50 p-4 rounded">
                                <img src={setup.qr_code_data_url} alt="QR" className="w-40 h-40" data-testid="qr-code" />
                            </div>
                            <div className="text-xs text-slate-500 text-center">Or enter secret manually:</div>
                            <div className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                                <code className="text-xs font-mono flex-1 truncate">{setup.secret}</code>
                                <button onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }} className="text-slate-500 hover:text-slate-900">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div><Label className="text-xs uppercase tracking-wider">Enter 6-digit code</Label>
                                <Input value={token} onChange={e => setToken(e.target.value)} placeholder="000000" maxLength={6} className="mt-1 font-mono text-lg text-center tracking-widest" data-testid="totp-token" /></div>
                            <Button onClick={verify} disabled={token.length !== 6} className="w-full bg-[#0A1C3E] rounded-sm" data-testid="verify-2fa">
                                Verify & Enable
                            </Button>
                        </div>
                    )}

                    {enabled && (
                        <div className="space-y-3">
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                                <div className="text-sm text-emerald-800">2FA is protecting your account.</div>
                            </div>
                            <div><Label className="text-xs uppercase tracking-wider">Enter code to disable</Label>
                                <Input value={disableToken} onChange={e => setDisableToken(e.target.value)} placeholder="000000" maxLength={6} className="mt-1 font-mono text-center tracking-widest" /></div>
                            <Button onClick={disable} disabled={disableToken.length !== 6} variant="outline" className="w-full">
                                Disable 2FA
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        </AppShell>
    );
}
